import {
   ArrowLeftMdIcon,
   ChevronRightIcon,
   SearchMagnifyingGlassIcon,
} from '@forwardflow/icons'
import {
   Button,
   ContentCard,
   Grid,
   Group,
   Select,
   Stack,
} from '@forwardflow/ui-v2'
import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import { motion } from 'framer-motion'
import { useGetFubUsers } from '@forwardflow/queries/onboarding.queries'
import { useEffect, useMemo, useState } from 'react'
import { TFubUser } from '@forwardflow/types/onboarding'
import { message } from 'antd'
import { CloseIcon, UserAddIcon, MailIcon } from '@forwardflow/icons'
import { CheckCircle02Icon } from '@forwardflow/icons'

export const TeamInvitation = () => {
   const { getNextStep, getPreviousStep, onboardingData, setInvitedUsers } =
      useOnboardingStoreV2()
   const { selectedTier, email: userEmail } = onboardingData
   const { data: fubUsersData } = useGetFubUsers(
      onboardingData?.organizationId ?? '',
      1000,
      1,
      onboardingData?.isFUBConnected
   )
   const fubUsers = useMemo(() => fubUsersData?.data || [], [fubUsersData])
   const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
   const [fubUser, setFubUser] = useState<TFubUser | null>(
      onboardingData?.invitedUsers?.find((u) => u.isOwner) ?? null
   )
   const [invitedTeamMembers, setInvitedTeamMembers] = useState<TFubUser[]>(
      onboardingData?.invitedUsers?.filter((u) => !u.isOwner) || []
   )

   const maxTeamMembers = (selectedTier?.includedSeats ?? 1) - 1

   const handleInviteUser = (selectedUserId: string | null) => {
      if (
         selectedTier?.tierType !== 'bundle' &&
         (invitedTeamMembers?.length ?? 0) >= maxTeamMembers
      ) {
         message.error(
            `You have reached the maximum number of team members (${maxTeamMembers})`
         )
         return
      }
      const invitedUser = fubUsers?.find(
         (user: TFubUser) => String(user.id) === String(selectedUserId)
      )
      const userExists = invitedTeamMembers?.some(
         (user) => String(user.id) === String(selectedUserId)
      )
      if (invitedUser && !userExists) {
         setInvitedTeamMembers([...(invitedTeamMembers || []), invitedUser])
      }
      setSelectedUserId(null)
   }

   const handleRemoveUser = (userId: string) => {
      setInvitedTeamMembers(
         invitedTeamMembers?.filter(
            (user) => String(user.id) !== String(userId)
         )
      )
   }

   const handleNextStep = () => {
      setInvitedUsers([
         ...invitedTeamMembers,
         { ...fubUser, isOwner: true } as TFubUser,
      ])
      getNextStep()
   }

   const shouldDisableInvite =
      selectedTier?.tierType !== 'bundle' &&
      (invitedTeamMembers?.length ?? 0) >= maxTeamMembers

   const foundAccount = useMemo(
      () => fubUsers?.find((u) => u.email === userEmail),
      [fubUsers, userEmail]
   )

   const userData = useMemo(() => {
      return fubUsers?.map((user: TFubUser) => ({
         value: String(user.id),
         label: `${user.firstName} ${user.lastName} - ${user.email}`,
      }))
   }, [fubUsers])

   const fubOwnerUserData = useMemo(() => {
      return fubUsers
         ?.map((user: TFubUser) => ({
            value: String(user.id),
            label: `${user.firstName} ${user.lastName} - ${user.email}`,
         }))
         .filter(
            (u) =>
               !invitedTeamMembers?.some(
                  (i) => String(i.id) === String(u.value)
               )
         )
   }, [fubUsers, invitedTeamMembers])

   useEffect(() => {
      if (foundAccount && !fubUser) {
         setFubUser(foundAccount)
      }
   }, [foundAccount, fubUser])

   return (
      <Stack gap={24}>
         <ContentCard
            className="space-y-2"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
         >
            <p className="text-white font-medium">
               Time to bring your team on board!
            </p>
            <p className="text-[#99A1AF] text-sm font-medium max-w-[600px]">
               Invite your team members so they can start using Forward Flow
               too.
            </p>
         </ContentCard>
         <ContentCard
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.3 }}
            className="space-y-6"
         >
            <Stack>
               <Select
                  searchable
                  value={String(fubUser?.id)}
                  data={fubOwnerUserData || []}
                  placeholder="Search for your Account"
                  label={
                     foundAccount ? (
                        <Group>
                           <CheckCircle02Icon width={16} color="#00D492" />
                           We found your account from Follow Up Boss!
                        </Group>
                     ) : (
                        'Select your Account from Follow Up Boss'
                     )
                  }
                  disabled={
                     !!foundAccount || onboardingData?.isOnBoardingCompleted
                  }
                  onChange={(value) => {
                     const selected =
                        fubUsers?.find((u) => String(u.id) === String(value)) ||
                        null
                     setFubUser(selected)
                     if (selected) {
                        setInvitedTeamMembers(
                           invitedTeamMembers?.filter(
                              (u) => String(u.id) !== String(selected.id)
                           )
                        )
                     }
                  }}
               />
            </Stack>
            {maxTeamMembers > 0 && (
               <Stack gap={4}>
                  <Group justify="space-between">
                     <p className="text-white/90 text-sm font-medium">
                        Invite Team Members
                     </p>
                     <p className="text-[#99A1AF] text-sm font-medium">
                        {invitedTeamMembers?.length} / {maxTeamMembers}
                     </p>
                  </Group>
                  <Select
                     leftSection={
                        <SearchMagnifyingGlassIcon color="#99A1AF" width={14} />
                     }
                     disabled={
                        shouldDisableInvite ||
                        onboardingData?.isOnBoardingCompleted
                     }
                     searchable
                     placeholder={
                        shouldDisableInvite
                           ? 'You have reached the maximum number of team members'
                           : 'Search for your Team members'
                     }
                     value={selectedUserId}
                     onChange={(value) => handleInviteUser(value)}
                     data={
                        userData?.filter(
                           (u) => String(u.value) !== String(fubUser?.id)
                        ) || []
                     }
                  />
               </Stack>
            )}
            {invitedTeamMembers && invitedTeamMembers?.length > 0 && (
               <Stack>
                  <p className="text-white/90 font-medium flex items-center gap-2">
                     <UserAddIcon width={24} color="#A684FF" /> Added Team
                     Members ({invitedTeamMembers?.length})
                  </p>
                  <Stack>
                     {invitedTeamMembers?.map((user) => (
                        <Group
                           key={user.id}
                           justify="space-between"
                           className="rounded-lg border border-white/20 bg-white/[0.02] p-3"
                        >
                           <Group>
                              <Group
                                 className="rounded-lg border border-[rgba(142,81,255,0.3)] bg-gradient-to-br from-[rgba(79,57,246,0.3)] to-[rgba(127,34,254,0.3)] w-8 h-8"
                                 justify="center"
                                 align="center"
                              >
                                 <MailIcon width={18} color="#A684FF" />
                              </Group>
                              <p className="text-white/90 font-medium text-sm">
                                 {user.firstName} {user.lastName}
                              </p>
                              <p className="text-[#99A1AF] text-sm font-medium">
                                 {user.email}
                              </p>
                           </Group>
                           {!onboardingData?.isOnBoardingCompleted && (
                              <CloseIcon
                                 width={16}
                                 height={16}
                                 className="cursor-pointer"
                                 onClick={() =>
                                    handleRemoveUser(String(user.id))
                                 }
                              />
                           )}
                        </Group>
                     ))}
                  </Stack>
               </Stack>
            )}
         </ContentCard>
         <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.6 }}
         >
            <Grid>
               <Grid.Col span={3}>
                  <Button
                     variant="secondary"
                     className="w-full"
                     onClick={() => getPreviousStep()}
                  >
                     <ArrowLeftMdIcon width={12} /> Back
                  </Button>
               </Grid.Col>
               <Grid.Col span={9}>
                  <Button
                     className="w-full"
                     onClick={handleNextStep}
                     disabled={!fubUser}
                  >
                     {invitedTeamMembers?.length === 0 || maxTeamMembers === 0 && 'Skip and'} Continue{' '}
                     <ChevronRightIcon width={18} />
                  </Button>
               </Grid.Col>
            </Grid>
         </motion.div>
      </Stack>
   )
}
